import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { projectsApi } from "@/lib/api";
import { Building2, Users2, Route, ChevronDown, ChevronUp, ExternalLink } from "lucide-react";

/**
 * Related Context — bidirectional relationship visibility for a
 * Project. Lean, additive card. Never redesigns the workspace and
 * never duplicates records; every field is derived from the
 * canonical graph via `GET /projects/{id}/connected-context`.
 *
 * Product spec (Connected Context v1):
 *   - Related Organizations (top 10 by relationship strength, See all
 *     expander). Project-tier first, then "Also active in this
 *     entity" as a separate subsection.
 *   - Related Contacts, provenance-labelled Direct / via <Org>.
 *   - Empty state renders quietly — never blocks the workspace.
 */

const REL_STATUS_COLORS = {
  "Partner": "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  "Outreach": "bg-blue-500/15 text-blue-300 border-blue-500/30",
  "Potential Fit": "bg-indigo-500/15 text-indigo-300 border-indigo-500/30",
  "Researching": "bg-amber-500/15 text-amber-200 border-amber-500/30",
  "Not Pursuing": "bg-white/5 text-white/40 border-white/10",
};

const ORG_STATUS_COLORS = {
  "Active": "text-emerald-300",
  "Inactive": "text-white/40",
  "Unknown": "text-white/40",
};

const TOP_ORG_CAP = 10;
const TOP_CONTACT_CAP = 10;

function Pill({ children, className = "", testid }) {
  return (
    <span
      data-testid={testid}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] uppercase tracking-wider ${className}`}
    >
      {children}
    </span>
  );
}

function OrgRow({ o, isEntityTier = false, testid }) {
  const relColor = REL_STATUS_COLORS[o.relationship_status] || REL_STATUS_COLORS["Researching"];
  const orgStatusColor = ORG_STATUS_COLORS[o.organization_status] || ORG_STATUS_COLORS["Unknown"];
  return (
    <div
      data-testid={testid}
      className="p-3 rounded-lg border border-white/10 bg-white/[0.02] hover:bg-white/[0.04] transition-colors"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <Link
              to={`/organizations/${o.organization_id}`}
              className="font-display font-medium text-sm text-white hover:text-white hover:underline underline-offset-4 flex items-center gap-1"
              data-testid={`related-org-link-${o.organization_id}`}
            >
              <Building2 className="w-3.5 h-3.5 text-white/40" />
              <span className="truncate max-w-[220px]">{o.name || "Unnamed Organization"}</span>
              <ExternalLink className="w-3 h-3 text-white/30" />
            </Link>
            <Pill className={relColor} testid={`related-org-status-${o.organization_id}`}>
              {o.relationship_status}
            </Pill>
            {o.organization_status && o.organization_status !== "Unknown" && (
              <span className={`text-[10px] uppercase tracking-wider ${orgStatusColor}`}>
                · {o.organization_status}
              </span>
            )}
          </div>
          {isEntityTier && o.on_project_name && (
            <div className="mt-1 text-[11px] text-white/40 flex items-center gap-1">
              <Route className="w-3 h-3" /> Also active in this entity → <span className="text-white/60">{o.on_project_name}</span>
            </div>
          )}
          {o.why_it_matters && (
            <p className="mt-1.5 text-xs text-white/70 line-clamp-2">{o.why_it_matters}</p>
          )}
          {o.matched_signals?.length > 0 && (
            <div className="mt-1.5 flex flex-wrap gap-1">
              {o.matched_signals.slice(0, 4).map((s, i) => (
                <span
                  key={`${o.organization_id}-sig-${i}`}
                  className="text-[10px] px-1.5 py-0.5 rounded bg-white/[0.04] text-white/60 border border-white/5"
                >
                  {s}
                </span>
              ))}
              {o.matched_signals.length > 4 && (
                <span className="text-[10px] text-white/40">+{o.matched_signals.length - 4}</span>
              )}
            </div>
          )}
          {(o.source_intake_item_id || o.added_by_name) && (
            <div className="mt-2 text-[10px] text-white/40 flex items-center gap-2 flex-wrap">
              {o.source_intake_item_id && <span>Added through Intake</span>}
              {o.added_by_name && <span>· by {o.added_by_name}</span>}
              {o.added_at && <span>· {new Date(o.added_at).toLocaleDateString()}</span>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ContactRow({ c, testid }) {
  const provenanceLabel =
    c.provenance === "direct"
      ? "Direct"
      : c.provenance === "via_organization"
      ? `via ${c.via_organization_name || "Organization"}`
      : c.provenance === "also_active_in_entity"
      ? `via ${c.via_organization_name || "Organization"} · Entity-level`
      : "";
  const provenanceColor =
    c.provenance === "direct"
      ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30"
      : "bg-white/[0.04] text-white/60 border-white/10";
  return (
    <div
      data-testid={testid}
      className="flex items-center justify-between gap-3 p-2.5 rounded-lg border border-white/10 bg-white/[0.02]"
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <Link
            to={`/contacts/${c.id}`}
            className="font-medium text-sm text-white hover:underline underline-offset-4 truncate max-w-[220px]"
            data-testid={`related-contact-link-${c.id}`}
          >
            {c.full_name || "Unnamed Contact"}
          </Link>
          <Pill className={provenanceColor} testid={`related-contact-provenance-${c.id}`}>
            {provenanceLabel}
          </Pill>
        </div>
        {(c.role || c.company) && (
          <div className="text-[11px] text-white/50 mt-0.5 truncate">
            {c.role}
            {c.role && c.company ? " · " : ""}
            {c.company}
          </div>
        )}
      </div>
    </div>
  );
}

export default function ProjectRelatedContext({ projectId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandOrgs, setExpandOrgs] = useState(false);
  const [expandContacts, setExpandContacts] = useState(false);
  const [showEntityOrgs, setShowEntityOrgs] = useState(false);
  const [showEntityContacts, setShowEntityContacts] = useState(false);

  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const r = await projectsApi.connectedContext(projectId);
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.status || "error");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  const orgs = data?.related_organizations || [];
  const entityOrgs = data?.entity_related_organizations || [];
  const contacts = data?.related_contacts || [];
  const entityContacts = data?.entity_related_contacts || [];

  const visibleOrgs = useMemo(
    () => (expandOrgs ? orgs : orgs.slice(0, TOP_ORG_CAP)),
    [orgs, expandOrgs]
  );
  const visibleContacts = useMemo(
    () => (expandContacts ? contacts : contacts.slice(0, TOP_CONTACT_CAP)),
    [contacts, expandContacts]
  );

  const isEmpty =
    !loading &&
    orgs.length === 0 &&
    entityOrgs.length === 0 &&
    contacts.length === 0 &&
    entityContacts.length === 0;

  // Silently no-op if the endpoint returned a permission-hidden 404,
  // otherwise the empty state matters (workspace should still show the
  // component with a neutral placeholder).
  if (error === 404) return null;

  return (
    <div
      className="mt-4 pt-4 border-t border-white/10 space-y-4"
      data-testid={`project-related-context-${projectId}`}
    >
      <div className="flex items-center gap-2">
        <h4 className="text-[11px] uppercase tracking-wider text-white/50 font-semibold">
          Related Context
        </h4>
        {loading && <span className="text-[10px] text-white/30">Loading…</span>}
      </div>

      {isEmpty && (
        <div
          className="text-xs text-white/40 italic"
          data-testid={`project-related-context-empty-${projectId}`}
        >
          No related organizations or contacts yet.
        </div>
      )}

      {(orgs.length > 0 || entityOrgs.length > 0) && (
        <div className="space-y-2" data-testid={`related-orgs-section-${projectId}`}>
          <div className="flex items-center gap-2 text-[11px] text-white/60">
            <Building2 className="w-3.5 h-3.5" />
            <span>Related Organizations</span>
            <span className="text-white/30">({orgs.length})</span>
          </div>
          {orgs.length === 0 && (
            <div className="text-xs text-white/40 italic pl-1">
              No organizations linked directly to this project.
            </div>
          )}
          <div className="space-y-2">
            {visibleOrgs.map((o) => (
              <OrgRow
                key={o.relationship_id || o.organization_id}
                o={o}
                testid={`related-org-row-${o.organization_id}`}
              />
            ))}
          </div>
          {orgs.length > TOP_ORG_CAP && (
            <button
              onClick={() => setExpandOrgs((v) => !v)}
              className="text-[11px] text-white/60 hover:text-white flex items-center gap-1 mt-1"
              data-testid={`related-orgs-expand-${projectId}`}
            >
              {expandOrgs ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              {expandOrgs ? "Show top 10" : `See all ${orgs.length}`}
            </button>
          )}

          {entityOrgs.length > 0 && (
            <div className="pt-2 mt-2 border-t border-white/5">
              <button
                onClick={() => setShowEntityOrgs((v) => !v)}
                className="text-[10px] uppercase tracking-wider text-white/40 hover:text-white/70 flex items-center gap-1"
                data-testid={`related-orgs-entity-toggle-${projectId}`}
              >
                {showEntityOrgs ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                Also active in this entity ({entityOrgs.length})
              </button>
              {showEntityOrgs && (
                <div className="mt-2 space-y-2">
                  {entityOrgs.map((o) => (
                    <OrgRow
                      key={o.relationship_id || `${o.organization_id}-${o.on_project_id || "ent"}`}
                      o={o}
                      isEntityTier
                      testid={`related-org-entity-row-${o.organization_id}`}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {(contacts.length > 0 || entityContacts.length > 0) && (
        <div className="space-y-2" data-testid={`related-contacts-section-${projectId}`}>
          <div className="flex items-center gap-2 text-[11px] text-white/60">
            <Users2 className="w-3.5 h-3.5" />
            <span>Related Contacts</span>
            <span className="text-white/30">({contacts.length})</span>
          </div>
          <div className="space-y-1.5">
            {visibleContacts.map((c) => (
              <ContactRow key={c.id} c={c} testid={`related-contact-row-${c.id}`} />
            ))}
          </div>
          {contacts.length > TOP_CONTACT_CAP && (
            <button
              onClick={() => setExpandContacts((v) => !v)}
              className="text-[11px] text-white/60 hover:text-white flex items-center gap-1 mt-1"
              data-testid={`related-contacts-expand-${projectId}`}
            >
              {expandContacts ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              {expandContacts ? "Show top 10" : `See all ${contacts.length}`}
            </button>
          )}

          {entityContacts.length > 0 && (
            <div className="pt-2 mt-2 border-t border-white/5">
              <button
                onClick={() => setShowEntityContacts((v) => !v)}
                className="text-[10px] uppercase tracking-wider text-white/40 hover:text-white/70 flex items-center gap-1"
                data-testid={`related-contacts-entity-toggle-${projectId}`}
              >
                {showEntityContacts ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                Also active in this entity ({entityContacts.length})
              </button>
              {showEntityContacts && (
                <div className="mt-2 space-y-1.5">
                  {entityContacts.map((c) => (
                    <ContactRow
                      key={c.id}
                      c={c}
                      testid={`related-contact-entity-row-${c.id}`}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
